import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { pad } from "../timeUtils";

/**
 * Package Placement Dialog (Iter-324 · Bulk Clipboard).
 *
 * Opens after the operator drags a Package Header from the clipboard
 * back onto the calendar. Renders every sub-service in the package
 * with its OWN Start Time + Care Team selector, pre-filled from the
 * drop position and each service's `duration_min`.
 *
 * Confirming calls the atomic `POST /appointments/package-unclip`
 * endpoint — either every sub-appointment lands or none do.
 *
 * Props:
 *   open       : boolean
 *   onClose    : () => void
 *   packageName: string
 *   items      : Array<{ id, service_id, service_name, duration_min,
 *                        customer_name, staff_id, package_id }>
 *   staffList  : full staff roster for the branch (id, full_name, service_ids)
 *   dropMinute : minutes-of-day where the package header was dropped
 *   dropStaffId: staff column the header was dropped onto
 *   onConfirm  : ({ moves }) => Promise<void>
 */
export default function PackagePlacementDialog({
  open, onClose, packageName, items, staffList, dropMinute, dropStaffId, onConfirm,
}) {
  const sortedItems = useMemo(
    () => [...(items || [])].sort((a, b) =>
      (a.service_name || "").localeCompare(b.service_name || "")
    ),
    [items],
  );

  // Sub-service placements — keyed by appointment id.
  //   time    : "HH:mm" string
  //   staffId : staff column
  const [placements, setPlacements] = useState({});

  // Pre-fill: first row → dropMinute + dropStaffId; each subsequent
  // row stacks after the previous row's duration.
  useEffect(() => {
    if (!open || !sortedItems.length) return;
    let cursor = Number.isFinite(dropMinute) ? dropMinute : 9 * 60;
    const draft = {};
    for (const it of sortedItems) {
      const hh = Math.floor(cursor / 60);
      const mm = cursor % 60;
      draft[it.id] = {
        time: `${pad(hh)}:${pad(mm)}`,
        staffId: dropStaffId || it.staff_id || "",
      };
      cursor += Math.max(15, parseInt(it.duration_min || 30, 10));
    }
    setPlacements(draft);
  }, [open, sortedItems, dropMinute, dropStaffId]);

  const setTime = (aid, time) =>
    setPlacements((p) => ({ ...p, [aid]: { ...(p[aid] || {}), time } }));
  const setStaff = (aid, staffId) =>
    setPlacements((p) => ({ ...p, [aid]: { ...(p[aid] || {}), staffId } }));

  const eligibleStaff = (serviceId) =>
    (staffList || []).filter((s) => {
      if (s.active === false) return false;
      if (!Array.isArray(s.service_ids)) return true;
      return s.service_ids.includes(serviceId);
    });

  const [busy, setBusy] = useState(false);
  const handleConfirm = async () => {
    if (busy) return;
    const moves = sortedItems.map((it) => {
      const p = placements[it.id] || {};
      return { appointment_id: it.id, time: p.time, staff_id: p.staffId };
    });
    // Client-side sanity check — the backend still guards on empty / bad.
    for (const m of moves) {
      if (!m.time || !/^\d{2}:\d{2}$/.test(m.time) || !m.staff_id) return;
    }
    setBusy(true);
    try {
      await onConfirm({ moves });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="package-placement-dialog">
        <DialogHeader>
          <DialogTitle>
            Place package: <span className="font-semibold">{packageName || "Package"}</span>
          </DialogTitle>
          <div className="text-xs text-muted-foreground mt-1" data-testid="package-placement-guest">
            {sortedItems[0]?.customer_name || "Walk-in"}
            {" — "}Assign a start time and care team to each service, then Confirm to place the whole package.
          </div>
        </DialogHeader>

        <div className="space-y-3 max-h-[55vh] overflow-y-auto py-1">
          {sortedItems.map((it) => {
            const p = placements[it.id] || { time: "", staffId: "" };
            const staffOptions = eligibleStaff(it.service_id);
            return (
              <div
                key={it.id}
                className="border border-border rounded-md p-3 bg-secondary/30"
                data-testid={`package-placement-row-${it.id}`}
              >
                <div className="text-sm font-medium mb-2">
                  {it.service_name}
                  <span className="ml-2 text-[11px] text-muted-foreground">
                    ({it.duration_min || 30} min)
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Start</Label>
                    <Input
                      type="time"
                      step="900"
                      value={p.time || ""}
                      onChange={(e) => setTime(it.id, e.target.value)}
                      data-testid={`package-placement-time-${it.id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Care Team</Label>
                    <Select
                      value={p.staffId || ""}
                      onValueChange={(v) => setStaff(it.id, v)}
                    >
                      <SelectTrigger data-testid={`package-placement-staff-${it.id}`}>
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent>
                        {staffOptions.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.full_name}
                          </SelectItem>
                        ))}
                        {staffOptions.length === 0 && (
                          <div className="px-2 py-1.5 text-xs text-muted-foreground">
                            No trained staff for this service.
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} data-testid="package-placement-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={busy || !sortedItems.length}
            data-testid="package-placement-confirm"
          >
            {busy ? "Placing…" : "Confirm Placement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
